const CAP4 = {
  id: 4,
  slug: "cap4-delegated-proof-of-stake",
  title: "Capítulo 4 — Delegated Proof of Stake (DPoS)",
  sections: [
    {
      heading: "¿Qué es Delegated Proof of Stake (DPoS)?",
      text: [
        "Delegated Proof of Stake, o **Prueba de Participación Delegada**, es una variación de Proof of Stake (PoS) en la que aparece una idea nueva: **además de hacer staking, los usuarios pueden votar** para elegir quién valida los bloques.",
        "En lugar de que cualquier nodo con monedas en staking pueda ser seleccionado directamente como validador, la red permite que los usuarios elijan a un número limitado de **delegados** (también llamados testigos), que serán los responsables de validar transacciones y producir nuevos bloques.",
        "Podemos imaginarlo como una especie de “democracia delegada” dentro de la blockchain: tú no validas directamente, pero eliges a quienes sí lo harán en tu nombre."
      ]
    },
    {
      heading: "Cómo encaja DPoS dentro de los algoritmos de consenso",
      text: [
        "En los capítulos anteriores vimos que los algoritmos de consenso buscan resolver un mismo problema: **cómo ponerse de acuerdo sobre qué transacciones son válidas** sin depender de una autoridad central.",
        "En Proof of Work (PoW), este acuerdo se logra mediante minería y trabajo computacional. En Proof of Stake (PoS), se logra poniendo monedas en juego (staking).",
        "DPoS toma la idea de PoS, pero añade una capa de **votación y representación**, donde los participantes eligen quién se encargará del trabajo de validación diaria."
      ]
    },
    {
      heading: "Participar en DPoS: staking y voto",
      text: [
        "En una red DPoS, los usuarios que poseen la criptomoneda nativa pueden **bloquear (stakear)** sus monedas para obtener **poder de voto**.",
        "Ese poder de voto se usa para apoyar a uno o varios candidatos a delegado. Cuantas más monedas tenga un usuario en staking, mayor será su peso en la votación.",
        "El proceso general suele seguir esta lógica:"
      ],
      list: [
        "Los usuarios bloquean sus monedas como staking.",
        "Cada usuario obtiene un poder de voto proporcional a la cantidad bloqueada.",
        "Con ese poder de voto eligen a delegados o testigos.",
        "Los delegados con más votos pasan a formar parte del grupo que valida bloques."
      ]
    },
    {
      heading: "¿Quiénes son los delegados o testigos?",
      text: [
        "Los **delegados** (o testigos) son los nodos elegidos por la comunidad para validar transacciones y producir bloques.",
        "No basta con que quieran ser delegados: normalmente deben cumplir ciertos requisitos técnicos, como tener buena conexión a Internet, hardware confiable y alta disponibilidad.",
        "Su reputación es clave: si actúan mal, validan bloques de forma negligente o dejan de estar disponibles, los votantes pueden retirar su apoyo y elegir a otras personas."
      ],
      list: [
        "Los delegados son responsables de incluir transacciones en los bloques.",
        "Reciben recompensas por su trabajo de validación.",
        "En muchos diseños, reparten una parte de esas recompensas entre los usuarios que los votaron."
      ]
    },
    {
      heading: "Ciclo de validación en una red DPoS",
      text: [
        "El funcionamiento diario de una red DPoS suele seguir un esquema similar al siguiente:"
      ],
      list: [
        "1. Se elige un conjunto de delegados activos (por ejemplo, un número fijo de validadores).",
        "2. La red organiza turnos o rondas en las que cada delegado tiene la oportunidad de producir un bloque.",
        "3. Si un delegado no cumple con su turno o intenta comportarse de forma deshonesta, puede perder reputación y votos.",
        "4. Periódicamente se actualiza la lista de delegados según los votos de los usuarios.",
        "5. El resultado final es una red que valida transacciones de forma rápida, con un grupo reducido pero rotativo de validadores."
      ]
    },
    {
      heading: "Recompensas e incentivos en DPoS",
      text: [
        "Para que el sistema funcione, tanto delegados como votantes deben tener **incentivos claros**:",
        "Los delegados reciben recompensas por el trabajo de validación (por ejemplo, comisiones de transacción o emisión nueva de monedas).",
        "Muchos diseños de DPoS permiten o incentivan que los delegados compartan una parte de esas recompensas con las personas que los votaron.",
        "De esta forma, los usuarios tienen motivos para participar en la votación y apoyar a delegados responsables."
      ],
      list: [
        "Los delegados quieren votos para recibir recompensas.",
        "Los usuarios quieren apoyar a delegados confiables para obtener una parte de esas recompensas.",
        "La red se beneficia cuando los delegados elegidos son técnicamente capaces y se comportan de forma honesta."
      ]
    },
    {
      heading: "Ventajas de Delegated Proof of Stake",
      text: [
        "DPoS se diseñó para mejorar algunos aspectos prácticos de otros mecanismos de consenso, especialmente en términos de rendimiento y participación."
      ],
      list: [
        "**Mayor rendimiento y eficiencia:** al tener un número limitado de delegados, la red puede organizar mejor los turnos de validación y procesar las transacciones con mayor rapidez.",
        "**Mayor participación de la comunidad:** incluso si no tienes conocimientos técnicos para operar un nodo, puedes participar votando a delegados.",
        "**Incentivos alineados:** delegados y votantes se benefician cuando la red funciona bien y mantiene buena reputación.",
        "**Buena escalabilidad:** menos nodos validando bloques facilitan llegar a consenso rápido."
      ]
    },
    {
      heading: "Riesgos y desventajas de DPoS",
      text: [
        "Aunque DPoS ofrece ventajas importantes, también trae riesgos que hay que conocer."
      ],
      list: [
        "**Posible centralización:** si siempre se vota a los mismos delegados, el control de la red se concentra en pocas manos.",
        "**Votaciones poco activas:** si la mayoría de usuarios no participa, un pequeño grupo puede decidir quién valida bloques.",
        "**Acuerdos entre delegados:** un conjunto de delegados podría coordinarse para beneficiarse entre ellos (por ejemplo, intercambiando votos o recompensas) en lugar de priorizar el bien de la red.",
        "**Dependencia de la reputación:** los usuarios necesitan información para saber a quién votar; si esa información es confusa o manipulada, la calidad de los delegados podría deteriorarse."
      ]
    },
    {
      heading: "¿Cuándo tiene sentido usar DPoS?",
      text: [
        "DPoS suele ser una buena opción para proyectos que buscan un equilibrio entre **descentralización, participación comunitaria y alto rendimiento**.",
        "Es especialmente atractivo cuando se necesitan muchas transacciones por segundo y una experiencia de usuario fluida, pero aun así se quiere que la comunidad tenga voz mediante el voto.",
        "Como siempre, no existe un algoritmo perfecto: DPoS es una pieza más del menú de diseño de blockchains, con fortalezas y debilidades propias."
      ]
    },
    {
      heading: "Resumen del capítulo",
      text: [
        "Delegated Proof of Stake (DPoS) introduce la idea de que los usuarios no solo ponen monedas en staking, sino que también **delegan su confianza mediante el voto**.",
        "En lugar de miles de validadores compitiendo entre sí, un grupo más pequeño de delegados es elegido para hacer el trabajo pesado de validar transacciones y producir bloques.",
        "Esto permite redes rápidas y eficientes, pero también exige vigilancia de la comunidad para evitar que la validación se vuelva demasiado centralizada.",
        "Entender DPoS te ayuda a leer con ojo crítico las promesas de muchos proyectos que hablan de “alta velocidad” y “gobernanza por la comunidad”: ahora sabes qué hay detrás de esos conceptos."
      ]
    }
  ]
};

export default CAP4;
